// Table
import { Table } from "./Table";
import { Entity } from "../Entity";
import { ExistenceStatus } from "../EntityStatus";
/**
 * EntityTable implementation - A wrapper on Table of IEntity<T> objects, and introduced following additional behavior:
 * 1. Entities can be edited directly using IEntity methods like pushDelta, revertDelta, updateBaseline, etc. These are internally
 *    observed and will update the table's indexes and views automatically.
 * 2. `set` method is not allowed, as an entity can't be set directly. Instead, the `add` method should be used.
 * 3. Views exclude soft-deleted entities.
 */ export class EntityTable extends Table {
    // #region CRUD OPERATIONS
    itemIds() {
        // Filter out soft-deleted items from the view
        return super.itemIds().filter((id)=>this._items[id].status !== ExistenceStatus.SoftDeleted);
    }
    add(id, value) {
        if (this.get(id)) {
            throw new Error(`IdAlreadyInUse [Table=${this.name}][Id=${id}]`);
        }
        const entity = this.createEntity(id, value);
        super.set(id, entity);
        return entity;
    }
    reKey(id, newId) {
        const changed = super.reKey(id, newId);
        // Update the id of the entity so that subsequent changes are correctly indexed
        this._items[newId].id = newId;
        return changed;
    }
    executeBatch(batch) {
        return super.executeBatch(()=>batch(this));
    }
    set() {
        /**
         * For IEntity objects, the regular push/commitDelta methods should be used to update the entity.
         * This is to ensure that any updates to the entity are correctly indexed and flagged for persistence.
         */ throw new Error(`SetNotAllowedOnAnEntityTable [Table=${this.name}]`);
    }
    // #endregion
    // #region PRIVATE HELPERS
    /**
     * Helper to create an observed entity object from a baseline value
     * @param id Entity id
     * @param baseline Baseline value of the entity
     */ createEntity(id, baseline) {
        return new ObservedEntity(baseline, id, (id)=>this.refresh(id), (id)=>this.flag(id));
    }
    /**
     * Create a new EntityTable
     * @param name The name of the table
     */ constructor(name){
        super(name), this.name = name, // All items in the table
        this._items = {};
    }
}
/**
 * Entity that intercepts updates made to an entity. This is a private class and is
 * exposed as only an IEntity<T> to the caller via the EntityTable.
 */ class ObservedEntity extends Entity {
    pushDelta(changes, deltaKey) {
        super.pushDelta(changes, deltaKey);
        this.onUpdate(this.id);
    }
    revertDelta(deltaKey) {
        super.revertDelta(deltaKey);
        this.onUpdate(this.id);
    }
    updateBaseline(changes) {
        super.updateBaseline(changes);
        this.onUpdate(this.id);
        this.flag(this.id); // Flag the entity for tracking whenever the baseline is updated
    }
    /**
     * Soft delete the entity - This removes the entry from the indexes while keeping it around
     * in the table so that it can be undeleted if needed.
     */ softDelete() {
        super.softDelete();
        this.onUpdate(this.id);
    }
    undelete() {
        super.undelete();
        this.onUpdate(this.id);
    }
    /**
     * Creates an instance of ObservedEntity.
     * @param baseline Baseline value of the entity
     * @param id Entity id
     * @param onUpdate Callback to notify when the entity is updated
     * @param flag Callback to flag the entity for tracking
     */ constructor(baseline, id, onUpdate, flag){
        super(baseline), this.id = id, this.onUpdate = onUpdate, this.flag = flag;
    }
}
/**
 * Helper to register an index on an entity table/partition using a definition based on entity's value (T).
 *
 * @param partition The entity table/partition to create the index on
 * @param indexName The name of the index
 * @param definition The index definition that operates on the entity's value (T)
 */ export function registerIndex(partition, indexName, definition) {
    return partition.registerIndex(indexName, indexDefinition(definition));
}
/**
 * Helper to apply a filter to an entity table/partition based on the entity's value (T).
 *
 * @param partition The entity table/partition to apply the filter on
 * @param filter The filter function that operates on the entity's value (T)
 * @template T The type of the entity's value
 */ export function applyFilter(partition, filter) {
    partition.applyFilter((entity, path)=>filter(entity.value, path));
}
/**
 * Helper to apply a comparator to an entity table/partition based on the entity's value (T).
 *
 * @param partition The entity table/partition to apply the comparator on
 * @param comparator The comparator function that operates on the entity's value (T)
 * @template T The type of the entity's value
 */ export function applyComparator(partition, comparator) {
    partition.applyComparator((a, b, path)=>comparator(a.value, b.value, path));
}
/**
 * Helper to create an index definition for an entity table based on a definition that operates on the entity's value (T).

* @param definition The index definition that operates on the entity's value (T)
 */ function indexDefinition(definition) {
    return (entity)=>{
        if (entity.status !== ExistenceStatus.SoftDeleted) {
            return definition(entity.value);
        }
        return null;
    };
}
